// @ts-nocheck
/**
 * WI Pack Products
 *
 * Renders the bottles contained in a wine pack inside a Swiper carousel
 *
 * Data source: the pack product's
 *   selected_or_first_available_variant.metafields.fgw.pack_products
 * (a list of { variant_id, quantity }), passed in via data-products. Each
 * bottle is hydrated via the FG Storefront GraphQL helper.
 *
 * @typedef PackProductData
 * @type {object}
 * @property {string} variant_id - Storefront ProductVariant gid
 * @property {number} quantity   - number of this bottle in the pack
 */

import { ThemeEvents } from '@theme/events';
import { FGStorefrontGraphQL } from '@fgtheme/graphql';

class WIPackProducts extends HTMLElement {
  #swiper = null;

  /** @type {PackProductData[]} */
  #packProducts = [];

  /** @type {{ id: string, product: any }[]} */
  #productData = [];

  #onVariantChangeBound = null;
  #flipMql = null;
  #applySemanticsBound = null;

  connectedCallback() {
    this.#onVariantChangeBound = this.#onVariantChange.bind(this);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantChangeBound);

    try {
      this.#packProducts = JSON.parse(this.dataset.products ?? '[]');
    } catch (e) {
      console.error('wi-pack-products: failed to parse pack_products JSON', e);
      this.#packProducts = [];
    }

    this.init();
  }

  disconnectedCallback() {
    if (this.#onVariantChangeBound) {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantChangeBound);
    }
    if (this.#flipMql && this.#applySemanticsBound) {
      this.#flipMql.removeEventListener('change', this.#applySemanticsBound);
    }
  }

  async init() {
    if (!this.#packProducts.length) return;
    await this.getProductData();
    this.renderProducts();
    this.initSwiper();
    this.bindFlip();
  }

  initSwiper() {
    const swiperElement = this.querySelector('.swiper');
    if (!swiperElement || typeof Swiper === 'undefined') return;
    this.#swiper = new Swiper(swiperElement, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      centeredSlides: true,
      loop: true,
      mousewheel: { releaseOnEdges: true, forceToAxis: true },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        clickable: true,
        dynamicBullets: window.matchMedia('(max-width: 749px)').matches,
        dynamicMainBullets: 7,
        renderBullet: (index, className) =>
          `<button type="button" class="${className}" aria-label="Go to bottle ${index + 1}"></button>`,
      },
      a11y: {
        prevSlideMessage: 'Previous bottle',
        nextSlideMessage: 'Next bottle',
      },
      autoHeight: true,
    });
  }

  bindFlip() {
    // bindFlip runs again after a variant change, so drop the old listener first
    if (this.#flipMql && this.#applySemanticsBound) {
      this.#flipMql.removeEventListener('change', this.#applySemanticsBound);
    }
    this.#flipMql = window.matchMedia('(min-width: 827px)');
    const cards = [...this.querySelectorAll('.card')];

    this.#applySemanticsBound = () => {
      cards.forEach((card) => {
        if (this.#flipMql.matches) {
          card.removeAttribute('role');
          card.removeAttribute('tabindex');
          card.removeAttribute('aria-pressed');
          card.classList.remove('is-flipped');
        } else {
          card.setAttribute('role', 'button');
          card.setAttribute('tabindex', '0');
          card.setAttribute('aria-label', 'Show wine details');
          card.setAttribute('aria-pressed', card.classList.contains('is-flipped') ? 'true' : 'false');
        }
      });
    };

    const toggle = (card) => {
      if (this.#flipMql.matches) return;
      const flipped = card.classList.toggle('is-flipped');
      card.setAttribute('aria-pressed', flipped ? 'true' : 'false');
    };

    cards.forEach((card) => {
      card.addEventListener('click', () => toggle(card));
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggle(card);
        }
      });
    });

    this.#applySemanticsBound();
    this.#flipMql.addEventListener('change', this.#applySemanticsBound);
  }

  /**
   * On `variant:update` (detail.resource.id is the variant id), re-fetch that
   * variant's pack_products and rebuild the carousel.
   * @param {*} event
   */
  async #onVariantChange(event) {
    const variantId = event?.detail?.resource?.id;
    if (variantId == null) return;

    const variantNode = await this.getVariantPackProducts(variantId);
    if (!variantNode?.packProducts?.value) return;
    try {
      this.#packProducts = JSON.parse(variantNode.packProducts.value);
    } catch (e) {
      console.error('wi-pack-products: failed to parse variant pack_products', e);
      return;
    }

    if (this.#swiper) this.#swiper.destroy(true, true);
    this.querySelector('.swiper')?.remove();
    this.querySelector('.inline-controls')?.remove();
    this.init();
  }

  async getProductData() {
    const query = {
      query: `
        query GetPackBottles($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              product {
                id
                title
                handle
                featuredImage { altText url(transform: { maxWidth: 600, maxHeight: 600 }) }
                metafield_country: metafield(namespace: "fgw", key: "country") { type value }
                metafield_profile: metafield(namespace: "fgw", key: "sweetness_scale") {
                  value
                  reference {
                    ... on Metaobject {
                      field(key: "title") {
                        value
                      }
                    }
                  }
                }
                metafield_body: metafield(namespace: "fgw", key: "body_scale") {
                  type 
                  value
                  reference {
                    ...on Metaobject {
                      field(key: "title") {
                        value
                      }
                    }
                  }
                }
                metafield_abv: metafield(namespace: "fgw", key: "abv") { type value }
                metafield_tastingNotes: metafield(namespace: "fgw", key: "tasting_notes") {
                  type
                  value
                  references(first: 10) {
                    nodes {
                      ... on Metaobject {
                        field(key: "title") {
                          value
                        }
                      }
                    }
                  } 
                }
                metafield_pairsWith: metafield(namespace: "fgw", key: "food_pairing") {
                  type 
                  value 
                  references(first: 10) {
                    nodes {
                      ... on Metaobject {
                        field(key: "title") {
                          value
                        }
                      }
                    }
                  }
                }
                metafield_ratingAvg: metafield(namespace: "yotpo", key: "reviews_average") { type value }
                metafield_ratingCount: metafield(namespace: "yotpo", key: "reviews_count") { type value }
              }
            }
          }
        }
      `,
      variables: { ids: this.#packProducts.map((d) => d.variant_id) },
    };
    try {
      const response = await FGStorefrontGraphQL(query);
      if (!response?.ok) {
        console.error('wi-pack-products: bottle data request failed', response?.status);
        this.#productData = [];
        return;
      }
      const result = await response.json();
      if (!result?.data?.nodes) {
        console.error('wi-pack-products: product data fetch failed', result);
        this.#productData = [];
        return;
      }
      this.#productData = result.data.nodes;
    } catch (e) {
      console.error('wi-pack-products: network error fetching bottle data', e);
      this.#productData = [];
    }
  }

  /**
   * @param {string} id - ProductVariant numeric id
   */
  async getVariantPackProducts(id) {
    const query = {
      query: `
        query GetVariantPackProducts($id: ID!) {
          node(id: $id) {
            ... on ProductVariant {
              id
              packProducts: metafield(namespace: "fgw", key: "pack_products") { value }
            }
          }
        }
      `,
      variables: { id: `gid://shopify/ProductVariant/${id}` },
    };
    try {
      const response = await FGStorefrontGraphQL(query);
      if (!response?.ok) {
        console.error('wi-pack-products: variant pack_products request failed', response?.status);
        return null;
      }
      const result = await response.json();
      return result?.data?.node;
    } catch (e) {
      console.error('wi-pack-products: network error fetching variant pack_products', e);
      return null;
    }
  }

  getBottleQuantity(variantId) {
    const found = this.#packProducts.find((p) => p.variant_id === variantId);
    return found ? found.quantity : 1;
  }

  renderProducts() {
    const containerTpl = this.querySelector('template[data-tpl="container"]');
    const productTpl = this.querySelector('template[data-tpl="product"]');
    const tagTpl = this.querySelector('template[data-tpl="tag"]');
    if (!containerTpl || !productTpl) return;

    const containerFragment = containerTpl.content.cloneNode(true);
    const carousel = containerFragment.querySelector('[data-area="carousel"]');

    this.#productData.forEach((variant) => {
      const fragment = productTpl.content.cloneNode(true);
      const titleEl = fragment.querySelector('[data-area="title"]');

      if (!variant || !variant.product) {
        if (titleEl) titleEl.textContent = 'Product unavailable';
        carousel.appendChild(fragment);
        return;
      }

      const product = variant.product;

      // Title (truncated to match live).
      if (titleEl) titleEl.textContent = this.truncate(product.title, 44);

      // Bottle quantity label.
      const qtyEl = fragment.querySelector('[data-area="quantity"]');
      if (qtyEl) {
        const qty = this.getBottleQuantity(variant.id);
        qtyEl.textContent = qty > 1 ? `${qty} Bottles` : `${qty} Bottle`;
      }

      // Bottle image.
      const imageEl = fragment.querySelector('[data-area="image"]');
      if (imageEl && product.featuredImage) {
        imageEl.innerHTML = `<img src="${product.featuredImage.url}" alt="${
          product.featuredImage.altText ?? product.title
        }" width="107" height="302" loading="lazy">`;
      }

      // Star rating (per bottle).
      const ratingEl = fragment.querySelector('[data-area="rating"]');
      if (ratingEl) {
        ratingEl.innerHTML = this.buildStars(
          parseFloat(product.metafield_ratingAvg?.value),
          parseInt(product.metafield_ratingCount?.value, 10)
        );
      }

      // Tags: country, profile, body.
      const tagsEl = fragment.querySelector('[data-area="tags"]');
      if (tagsEl && tagTpl) {
        [
          product.metafield_country?.value,
          product.metafield_profile?.reference.field.value,
          product.metafield_body?.reference.field.value,
        ]
          .filter(Boolean)
          .forEach((label) => {
            const tag = tagTpl.content.cloneNode(true);
            tag.querySelector('span').textContent = label;
            tagsEl.appendChild(tag);
          });
      }

      // Additional info rows (hidden when empty).
      const abv = product.metafield_abv?.value;
      this.fillRow(fragment, 'abv', abv ? `${abv}%` : '');
      this.fillRow(fragment, 'tasting', product.metafield_tastingNotes?.references.nodes.map(n => n.field.value).join(', '));
      this.fillRow(fragment, 'pairs', product.metafield_pairsWith?.references.nodes.map(n => n.field.value).join(', '));

      carousel.appendChild(fragment);
    });

    this.appendChild(containerFragment);
  }

  /**
   * Fill an additional-info row's value, or hide the row when there's no data.
   * @param {DocumentFragment} fragment
   * @param {string} row
   * @param {string} value
   */
  fillRow(fragment, row, value) {
    const rowEl = fragment.querySelector(`[data-row="${row}"]`);
    if (!rowEl) return;
    if (!value) {
      rowEl.hidden = true;
      return;
    }
    rowEl.hidden = false;
    const textEl = rowEl.querySelector(`[data-area="${row}"]`);
    if (textEl) textEl.textContent = value;
  }

  /**
   * @param {string} str
   * @param {number} max
   */
  truncate(str, max) {
    if (!str) return '';
    return str.length > max ? `${str.slice(0, max)}…` : str;
  }

  /**
   * Inline SVG star row from the yotpo review metafields. Empty string when
   * the bottle has no reviews.
   * @param {number} rating
   * @param {number} count
   */
  buildStars(rating, count) {
    if (!(rating > 0) || !(count > 0)) return '';

    // .3–.7 rounds to a half star, matching wi-fg-review
    const decimal = rating % 1;
    let filled = Math.floor(rating);
    let half = false;
    if (decimal > 0.7) filled += 1;
    else if (decimal >= 0.3) half = true;

    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const state = i <= filled ? ' filled' : half && i === filled + 1 ? ' half' : '';
      stars.push(
        `<svg class="pack-star${state}" viewBox="0 0 18 18" role="presentation">` +
          '<use href="#wi-pack-star" class="star-fill"></use>' +
          '<use href="#wi-pack-star" class="star-outline"></use>' +
          '</svg>'
      );
    }
    return `<div class="pack-stars" role="img" aria-label="Rated ${rating} out of 5 stars">${stars.join('')}</div>`;
  }

  /**
   * WI list metafields (pairs_with, tasting_notes, …) are stored as JSON arrays.
   * @param {string | undefined} value
   * @returns {string[]}
   */
  parseListMetafield(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [value];
    } catch (e) {
      return [value];
    }
  }
}

if (!customElements.get('wi-pack-products')) {
  customElements.define('wi-pack-products', WIPackProducts);
}

export { WIPackProducts };
